import React, { useEffect, useState } from 'react';
import PageMeta from '../components/common/PageMeta';
import PageBreadcrumb from '../components/common/PageBreadCrumb';
import FileItemRow from '../components/files/FileItemRow';
import { filesService, FileItem } from '../services/FilesService';
import { processingService } from '../services/ProcessingService';
import { issueReportingService } from '../services/IssueReportingService';
import {
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
} from "../components/ui/table";
import CsvPreview from '../components/files/CsvPreview';
import { ChevronDown, ChevronUp, LucideTrash2 } from 'lucide-react';
import Button from '../components/ui/button/Button';
import DeleteAllConfirmation from '../components/files/DeleteAllConfirmation';
import ReportDialog from '../components/files/ReportDialog';
import { userStateService } from '../services/UserStateService';
import { supabaseService } from '../integrations/supabase/client';
import Alert from '../components/ui/alert/Alert';

// Define sort types
type SortField = 'fileName' | 'processedDate' | null;
type SortDirection = 'asc' | 'desc';

// Alert types definition
interface AlertData {
    show: boolean;
    variant: "success" | "error" | "warning" | "info";
    title: string;
    message: string;
    duration?: number;
    showProgress?: boolean;
}

// Add sanitization function to prevent XSS
const sanitizeText = (text: string): string => {
    if (!text) return '';
    // Create a temporary div element
    const temp = document.createElement('div');
    // Set its text content (which automatically escapes HTML)
    temp.textContent = text;
    // Return the sanitized string
    return temp.innerHTML;
};

// Sanitize an object's string values
const sanitizeObject = (obj: Record<string, any>): Record<string, any> => {
    const result: Record<string, any> = {};
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            const value = obj[key];
            if (typeof value === 'string') {
                result[key] = sanitizeText(value);
            } else {
                result[key] = value;
            }
        }
    }
    return result;
};

export default function FilesTable() {
    const [files, setFiles] = useState<FileItem[]>([]);
    const [previewData, setPreviewData] = useState<Record<string, string>[] | null>(null);
    const [previewFileName, setPreviewFileName] = useState<string>('');
    const [sortField, setSortField] = useState<SortField>('processedDate');
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [fileToReport, setFileToReport] = useState<FileItem | null>(null);
    const [showReportDialog, setShowReportDialog] = useState(false);
    const [isReprocessing, setIsReprocessing] = useState<string | null>(null);
    const [alert, setAlert] = useState<AlertData | null>(null);

    useEffect(() => {
        const loadFiles = async () => {
            const allFiles = await filesService.getAllFiles();
            sortFiles(allFiles, sortField, sortDirection);
        };
        loadFiles();
    }, []);

    // Function to sort files
    const sortFiles = (filesToSort: FileItem[], field: SortField, direction: SortDirection) => {
        if (!field) {
            setFiles([...filesToSort]);
            return;
        }

        const sortedFiles = [...filesToSort].sort((a, b) => {
            if (field === 'fileName') {
                const nameA = a.pdfFileName.toLowerCase();
                const nameB = b.pdfFileName.toLowerCase();
                return direction === 'asc'
                    ? nameA.localeCompare(nameB)
                    : nameB.localeCompare(nameA);
            } else if (field === 'processedDate') {
                const dateA = new Date(a.lastProcessedDate).getTime();
                const dateB = new Date(b.lastProcessedDate).getTime();
                return direction === 'asc' ? dateA - dateB : dateB - dateA;
            }
            return 0;
        });

        setFiles(sortedFiles);
    };

    // Handle sort header click
    const handleSort = (field: SortField) => {
        let newDirection: SortDirection = 'asc';

        if (field === sortField) {
            // Toggle direction if same field is clicked
            newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
        }

        setSortField(field);
        setSortDirection(newDirection);
        sortFiles(files, field, newDirection);
    };

    // Get sort indicator
    const getSortIndicator = (field: SortField) => {
        if (sortField !== field) return null;

        return sortDirection === 'asc'
            ? <ChevronUp className="inline-block ml-1 h-4 w-4" />
            : <ChevronDown className="inline-block ml-1 h-4 w-4" />;
    };

    const handleDownload = async (fileId: string) => {
        try {
            const file = await filesService.getFile(fileId);
            if (file && file.parsedResults) {
                // Parse the JSON string back to an object and sanitize
                const parsedRawData = JSON.parse(file.parsedResults) as Record<string, string>[];
                // Sanitize each object in the array
                const parsedData = parsedRawData.map(item => sanitizeObject(item));

                // Get all unique headers from all rows
                const headers = Array.from(
                    new Set(
                        parsedData.flatMap(row => Object.keys(row))
                    )
                );

                // Pre-analyze columns to determine which ones need escaping
                const columnsRequiringQuotes = new Set<string>();

                // Scan each column to see if any cell requires quoting
                headers.forEach(header => {
                    const needsQuoting = parsedData.some(row => {
                        const value = row[header] || '';
                        return value.includes(',') || value.includes('"') || value.includes('\n');
                    });

                    if (needsQuoting) {
                        columnsRequiringQuotes.add(header);
                    }
                });

                // Convert parsed data to CSV format
                let csvContent = '';

                // Add headers row
                csvContent += headers.join(',') + '\n';

                // Add data rows with consistent quoting by column
                parsedData.forEach(row => {
                    const csvRow = headers.map(header => {
                        const cellValue = row[header] || '';

                        // If this column requires quoting, quote all values in it
                        if (columnsRequiringQuotes.has(header)) {
                            return `"${cellValue.replace(/"/g, '""')}"`;
                        }
                        return cellValue;
                    }).join(',');
                    csvContent += csvRow + '\n';
                });

                // Create and download the file
                const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');

                // Create filename by replacing the original extension with .csv
                const originalFileName = file.pdfFileName;
                const csvFileName = originalFileName.substring(0, originalFileName.lastIndexOf('.')) + '.csv';

                link.href = url;
                link.setAttribute('download', csvFileName);
                document.body.appendChild(link);
                link.click();

                // Clean up
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            } else {
                console.error('File not found or has no parsed results');
            }
        } catch (error) {
            console.error('Error downloading CSV file:', error);
        }
    };

    const handleView = async (fileId: string) => {
        try {
            const file = await filesService.getFile(fileId);
            if (file && file.parsedResults) {
                // Parse the JSON string back to an object and sanitize
                const parsedRawData = JSON.parse(file.parsedResults) as Record<string, string>[];
                // Sanitize each object in the array
                const parsedData = parsedRawData.map(item => sanitizeObject(item));
                setPreviewData(parsedData);
                setPreviewFileName(sanitizeText(file.pdfFileName));
            } else {
                console.error('File not found or has no parsed results');
            }
        } catch (error) {
            console.error('Error previewing file:', error);
        }
    };

    const handleReprocess = async (fileId: string) => {
        try {
            // Set the file ID being reprocessed to show loading state
            setIsReprocessing(fileId);
            
            // Use the ProcessingService to reprocess the file
            const result = await processingService.reprocessFile(fileId);
            
            if (result.success) {
                // If successful, refresh the file list
                const allFiles = await filesService.getAllFiles();
                sortFiles(allFiles, sortField, sortDirection);
                
                // Show success message
                setAutoClosingAlert({
                    show: true,
                    variant: "success",
                    title: "File Reprocessed",
                    message: "Your file has been successfully reprocessed"
                });
            } else {
                // Show error message
                setAutoClosingAlert({
                    show: true,
                    variant: "error",
                    title: "Reprocessing Failed",
                    message: result.message || "There was an error reprocessing your file"
                });
            }
        } catch (error) {
            console.error('Error reprocessing file:', error);
            setAutoClosingAlert({
                show: true,
                variant: "error",
                title: "Reprocessing Failed",
                message: "There was an unexpected error reprocessing your file"
            });
        } finally {
            // Clear the reprocessing state
            setIsReprocessing(null);
        }
    };

    const handleReport = async (fileId: string) => {
        try {
            const fileItem = await filesService.getFile(fileId);
            if (fileItem) {
                setFileToReport(fileItem);
                setShowReportDialog(true);
            }
        } catch (error) {
            console.error('Error getting file for report:', error);
        }
    };

    const handleSubmitReport = async (reason: string, includePdf: boolean) => {
        if (!fileToReport) return;

        try {
            const result = await issueReportingService.submitReport(fileToReport, reason, includePdf);
            
            if (result.success) {
                setShowReportDialog(false);
                setFileToReport(null);
                
                // Show success message
                setAutoClosingAlert({
                    show: true,
                    variant: "success",
                    title: "Report Submitted",
                    message: "Your report has been successfully submitted"
                });
            } else {
                // Show error message
                setAutoClosingAlert({
                    show: true,
                    variant: "error",
                    title: "Report Submission Failed",
                    message: result.message || "There was an error submitting your report"
                });
            }
        } catch (error) {
            console.error('Error submitting report:', error);
            setAutoClosingAlert({
                show: true,
                variant: "error",
                title: "Report Submission Failed",
                message: "There was an unexpected error submitting your report"
            });
        }
    };

    const handleDelete = async (fileId: string) => {
        try {
            await filesService.deleteFile(fileId);
            // Refresh the files list after deletion
            const allFiles = await filesService.getAllFiles();
            sortFiles(allFiles, sortField, sortDirection);
        } catch (error) {
            console.error('Error deleting file:', error);
        }
    };

    const closePreview = () => {
        setPreviewData(null);
    };

    const handleDeleteAll = async () => {
        try {
            await filesService.deleteAllFiles();
            setFiles([]);
            setShowDeleteConfirmation(false);
        } catch (error) {
            console.error('Error deleting all files:', error);
        }
    };

    // Alert handling function
    const setAutoClosingAlert = (alertData: {
        show: boolean;
        variant: "success" | "error" | "warning" | "info";
        title: string;
        message: string;
    }) => {
        setAlert({
            ...alertData,
            duration: 5000, // 5 seconds
            showProgress: true
        });
        
        const timeoutId = setTimeout(() => {
            setAlert(null);
        }, 5000);

        return () => clearTimeout(timeoutId);
    };

    return (
        <>
            <PageMeta
                title="Files"
                description="View and manage your processed files"
            />
            <PageBreadcrumb pageTitle="Files" />
            
            <div className="mb-6 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-100 dark:border-gray-700">
                <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    All files are stored locally in your browser on your device.
                </p>
            </div>

            {alert && alert.show && (
                <div className="mb-6">
                    <Alert
                        variant={alert.variant}
                        title={alert.title}
                        message={alert.message}
                        onClose={() => setAlert(null)}
                        duration={alert.duration || 5000}
                        showProgress={alert.showProgress !== false}
                    />
                </div>
            )}

            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
                <div className="max-w-full overflow-x-auto">
                    <div className="min-w-full">
                        <Table>
                            <TableHeader className="border-b border-gray-100 dark:border-white/[0.05]">
                                <TableRow>
                                    <TableCell
                                        isHeader
                                        className="px-4 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                                    >
                                        <div
                                            className="flex items-center cursor-pointer hover:text-gray-700 dark:hover:text-gray-200"
                                            onClick={() => handleSort('fileName')}
                                        >
                                            File Name
                                            {getSortIndicator('fileName')}
                                        </div>
                                    </TableCell>
                                    <TableCell
                                        isHeader
                                        className="px-4 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                                    >
                                        <div
                                            className="flex items-center cursor-pointer hover:text-gray-700 dark:hover:text-gray-200"
                                            onClick={() => handleSort('processedDate')}
                                        >
                                            Processed Date
                                            {getSortIndicator('processedDate')}
                                        </div>
                                    </TableCell>
                                    <TableCell
                                        isHeader
                                        className="px-4 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                                    >
                                        <div className="flex items-center justify-between">
                                            <span>Actions</span>
                                            {files.length > 0 && (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => setShowDeleteConfirmation(true)}
                                                    className="border-red-300 text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-500 dark:hover:bg-red-900/20 py-1 px-2"
                                                    startIcon={<LucideTrash2 className="w-3.5 h-3.5" />}
                                                >
                                                    <span className="text-xs">Delete All</span>
                                                </Button>
                                            )}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            </TableHeader>

                            <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                                {files.length === 0 ? (
                                    <TableRow>
                                        <TableCell
                                            className="px-4 py-6 text-center text-gray-500 dark:text-gray-400"
                                        >
                                            No files have been processed yet
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    files.map(file => (
                                        <FileItemRow
                                            key={file.fileItemId}
                                            file={file}
                                            onDownload={handleDownload}
                                            onView={handleView}
                                            onReprocess={handleReprocess}
                                            onReport={handleReport}
                                            onDelete={handleDelete}
                                            isReprocessing={isReprocessing === file.fileItemId}
                                        />
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </div>
            </div>

            {/* Preview Overlay */}
            {previewData && (
                <CsvPreview
                    data={previewData}
                    onClose={closePreview}
                    fileName={previewFileName}
                />
            )}

            {/* Delete All Confirmation Modal */}
            <DeleteAllConfirmation
                isOpen={showDeleteConfirmation}
                onClose={() => setShowDeleteConfirmation(false)}
                onConfirm={handleDeleteAll}
            />

            {/* Add ReportDialog */}
            <ReportDialog
                isOpen={showReportDialog}
                onClose={() => setShowReportDialog(false)}
                onSubmit={handleSubmitReport}
                fileItem={fileToReport}
            />
        </>
    );
}
